<div class="fbschema-wrapper cpcontent">
	<h2>Firebase Scenarios</h2>
	<div *ngIf="!isAuthenticatedWithFirebase" class="mb-3">
		<button class="btn btn-primary btn-sm" (click)="signInWithGoogle()">Sign in with Google</button>
	</div>

	<div *ngIf="isAuthenticatedWithFirebase">
		<div class="row mb-3 align-items-start">
			<div class="col-12 col-md-6 mb-2 mb-md-0">
				<label for="scenarioSelect" class="form-label">Select scenario:</label><br>
				<select id="scenarioSelect" class="form-select" #sel (change)="onSelectScenario(sel.value)">
					<option value="">-- choose --</option>
					<option *ngFor="let id of scenarioIds" [value]="id">{{ id }}</option>
				</select>
			</div>
			<div class="col-12 col-md-6">
				<h5 class="mb-1">Info on Scenario Classes</h5>
				<p class="small mb-2">Use this tool to compare the Firebase JSON structure for a scenario with the TypeScript class definitions in ScenarioCLasses.ts.
					It highlights missing and additional fields for quick schema alignment.</p>
				<ul class="small ps-3 mb-2">
					<li><strong>Scenario Class</strong>: Top-level properties of the scenario object.</li>
					<li><strong>Components / Interactivities / Modifiers / Lesson Plan</strong>: Arrays with items that may have class-specific required fields.</li>
					<li><strong>Missing Fields</strong>: Expected by class, absent in Firebase.</li>
					<li><strong>Additional Fields</strong>: Present in Firebase, not defined in the class template (or type mismatch).</li>
				</ul>
				<div class="small border rounded p-2 bg-light">
					<strong>Legend:</strong>
					<span class="ms-2"><span class="diff-ok">✔</span> = All expected fields present</span>
					<span class="ms-2"><span class="diff-bad">✖</span> = One or more missing / extra fields</span>
				</div>
			</div>
		</div>

		<div *ngIf="selectedScenarioJson" class="schema-grid-4">
			<div class="col col-scenario">
				<h5>Scenario Class</h5>
				<p class="small text-muted mb-1">Fields detected in Firebase for this scenario (by level) with inferred type.</p>
				<table class="table table-sm table-bordered" *ngIf="foundFields.length; else noFound">
					<thead><tr><th>Level</th><th>Field</th><th>Type</th></tr></thead>
					<tbody>
						<tr *ngFor="let f of foundFields"><td>{{ f.level }}</td><td>{{ f.field }}</td><td>{{ f.type }}</td></tr>
					</tbody>
				</table>
				<ng-template #noFound><div class="text-muted small">None</div></ng-template>

				<!-- Components list -->
				<div class="mt-3">
					<h6>Components (one class for all)</h6>
					<table class="table table-sm table-bordered" *ngIf="scenario?.components?.length; else noComponents">
						<thead><tr><th>#</th><th>Id</th><th>Diff</th></tr></thead>
						<tbody>
							<tr *ngFor="let comp of scenario?.components; let i = index">
								<td>{{ i }}</td>
								<td>{{ getItemId(comp) }}</td>
								<td [ngClass]="componentHasDiff(i) ? 'diff-bad' : 'diff-ok'">{{ componentHasDiff(i) ? '✖' : '✔' }}</td>
							</tr>
						</tbody>
					</table>
					<ng-template #noComponents><div class="text-muted small">No components</div></ng-template>
				</div>

				<!-- Interactivities list -->
				<div class="mt-3">
					<h6>Interactivities (many different classes)</h6>
					<table class="table table-sm table-bordered" *ngIf="scenario?.interactivities?.length; else noInteractivities">
						<thead><tr><th>#</th><th>Name</th><th>Class</th><th>Diff</th></tr></thead>
						<tbody>
							<tr *ngFor="let ia of scenario?.interactivities; let i = index">
								<td>{{ i }}</td>
								<td>{{ getItemId(ia) }}</td>
								<td>{{ ia?.class || '(no class)' }}</td>
								<td [ngClass]="interactivityHasDiff(i) ? 'diff-bad' : 'diff-ok'">{{ interactivityHasDiff(i) ? '✖' : '✔' }}</td>
							</tr>
						</tbody>
					</table>
					<ng-template #noInteractivities><div class="text-muted small">No interactivities</div></ng-template>
				</div>

				<!-- Modifiers list -->
				<div class="mt-3">
					<h6>Modifiers</h6>
					<table class="table table-sm table-bordered" *ngIf="scenario?.modifiers?.length; else noModifiers">
						<thead><tr><th>#</th><th>Id</th><th>Class</th><th>Diff</th></tr></thead>
						<tbody>
							<tr *ngFor="let mod of scenario?.modifiers; let i = index">
								<td>{{ i }}</td>
								<td>{{ getItemId(mod) }}</td>
								<td>{{ mod?.class || '(no class)' }}</td>
								<td [ngClass]="modifierHasDiff(i) ? 'diff-bad' : 'diff-ok'">{{ modifierHasDiff(i) ? '✖' : '✔' }}</td>
							</tr>
						</tbody>
					</table>
					<ng-template #noModifiers><div class="text-muted small">No modifiers</div></ng-template>
				</div>

				<!-- Lessonplan list -->
				<div class="mt-3">
					<h6>Lesson Plan</h6>
					<table class="table table-sm table-bordered" *ngIf="scenario?.lessonplan?.length; else noLessonplan">
						<thead><tr><th>#</th><th>Id</th><th>Name</th><th>Class</th><th>Diff</th></tr></thead>
						<tbody>
							<tr *ngFor="let lp of scenario?.lessonplan; let i = index">
								<td>{{ i }}</td>
								<td>{{ getItemId(lp) }}</td>
								<td>{{ lp?.name || '(no name)' }}</td>
								<td>{{ lp?.class || '(no class)' }}</td>
								<td [ngClass]="lessonPlanStepHasDiff(i) ? 'diff-bad' : 'diff-ok'">{{ lessonPlanStepHasDiff(i) ? '✖' : '✔' }}</td>
							</tr>
						</tbody>
					</table>
					<ng-template #noLessonplan><div class="text-muted small">No lessonplan items</div></ng-template>
				</div>
			</div>
			<div class="col col-missing">
				<h5>Missing Fields</h5>
				<p class="small text-muted mb-1">Defined in the class but absent in Firebase JSON.</p>
				<table class="table table-sm table-bordered" *ngIf="missingFields.length; else noMissing">
					<thead><tr><th>Level</th><th>Field</th></tr></thead>
					<tbody>
						<tr *ngFor="let m of missingFields"><td>{{ m.level }}</td><td>{{ m.field }}</td></tr>
					</tbody>
				</table>
				<ng-template #noMissing><div class="text-muted small">None</div></ng-template>
			</div>
			<div class="col col-additional">
				<h5>Additional Fields</h5>
				<p class="small text-muted mb-1">Exist in Firebase but not in the class (or flagged type mismatch).</p>
				<table class="table table-sm table-bordered" *ngIf="additionalFields.length; else noAdditional">
					<thead><tr><th>Level</th><th>Field</th></tr></thead>
					<tbody>
						<tr *ngFor="let a of additionalFields"><td>{{ a.level }}</td><td>{{ a.field }}</td></tr>
					</tbody>
				</table>
				<ng-template #noAdditional><div class="text-muted small">None</div></ng-template>
			</div>
			<div class="col col-json">
				<h5>Raw JSON ({{ selectedScenarioId }})</h5>
				<p class="small text-muted mb-1">Pretty-printed source from Firebase for reference.</p>
				<pre class="json-viewer">{{ selectedScenarioJson }}</pre>
			</div>
		</div>
	</div>
</div>
